import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle, ChevronRight, Search } from 'lucide-react';
import type { Facetas, FiltroSped } from '../../../api/tipos';
import {
  Botao,
  CaixaSelecao,
  Campo,
  cx,
  Etiqueta,
} from '../../../componentes/ui';
import { numero, plural } from '../../../util/formato';
import {
  EMISSAO,
  MESES,
  MODELO,
  OPERACAO,
  SITUACAO,
  SITUACOES_SEM_XML,
} from './rotulos';
import css from './Filtros.module.css';

/**
 * Painel de filtros da análise.
 *
 * Duas regras que valem para todos os blocos:
 *
 * 1. **Nada marcado = tudo incluído.** É o oposto do padrão de checkbox, e por
 *    isso está escrito na tela. Sem isso, o usuário abre a análise e vê zero
 *    notas até marcar alguma coisa.
 * 2. **A contagem ao lado de cada opção já considera os OUTROS filtros.** O
 *    número significa "quantas notas eu tenho se marcar isto", não um total
 *    fixo que ignora o resto da seleção.
 */

interface Props {
  facetas: Facetas;
  filtro: FiltroSped;
  aoMudar: (f: FiltroSped) => void;
}

export function Filtros({ facetas, filtro, aoMudar }: Props) {
  /** Liga/desliga um valor numa das listas do filtro. */
  function alternar(campo: keyof FiltroSped, valor: string) {
    const atual = filtro[campo];
    if (!Array.isArray(atual)) return;
    const novo = atual.includes(valor)
      ? atual.filter((v) => v !== valor)
      : [...atual, valor];
    aoMudar({ ...filtro, [campo]: novo });
  }

  function definir(campo: keyof FiltroSped, valores: string[]) {
    aoMudar({ ...filtro, [campo]: valores });
  }

  return (
    <div className={css.painel}>
      <div className={css.principais}>
        <Competencias
          facetas={facetas}
          selecionadas={filtro.competencias}
          aoDefinir={(v) => definir('competencias', v)}
        />

        <Cfops
          facetas={facetas}
          selecionados={filtro.cfops}
          aoAlternar={(c) => alternar('cfops', c)}
          aoDefinir={(v) => definir('cfops', v)}
        />
      </div>

      <div className={css.secundarios}>
        <Bloco
          titulo="Tipo de operação"
          selecionados={filtro.operacoes}
          aoLimpar={() => definir('operacoes', [])}
        >
          {facetas.operacoes.length === 0 ? (
            <Nenhum />
          ) : (
            facetas.operacoes.map((o) => (
              <Opcao
                key={o.operacao}
                marcada={filtro.operacoes.includes(o.operacao)}
                rotulo={OPERACAO[o.operacao] ?? o.operacao}
                contagem={o.notas}
                aoClicar={() => alternar('operacoes', o.operacao)}
              />
            ))
          )}
        </Bloco>

        <Bloco
          titulo="Origem da emissão"
          selecionados={filtro.emissoes}
          aoLimpar={() => definir('emissoes', [])}
        >
          {facetas.emissoes.length === 0 ? (
            <Nenhum />
          ) : (
            facetas.emissoes.map((e) => (
              <Opcao
                key={e.emissao}
                marcada={filtro.emissoes.includes(e.emissao)}
                rotulo={EMISSAO[e.emissao] ?? e.emissao}
                contagem={e.notas}
                aoClicar={() => alternar('emissoes', e.emissao)}
              />
            ))
          )}
        </Bloco>

        <Bloco
          titulo="Modelo do documento"
          selecionados={filtro.modelos}
          aoLimpar={() => definir('modelos', [])}
        >
          {facetas.modelos.length === 0 ? (
            <Nenhum />
          ) : (
            facetas.modelos.map((m) => (
              <Opcao
                key={m.modelo}
                marcada={filtro.modelos.includes(m.modelo)}
                rotulo={
                  MODELO[m.modelo]
                    ? `${MODELO[m.modelo]} (${m.modelo})`
                    : m.modelo
                }
                contagem={m.notas}
                aoClicar={() => alternar('modelos', m.modelo)}
              />
            ))
          )}
        </Bloco>

        <Bloco
          titulo="Situação"
          selecionados={filtro.situacoes}
          aoLimpar={() => definir('situacoes', [])}
          nota="Cancelada, denegada e inutilizada não têm XML para baixar."
        >
          {facetas.situacoes.length === 0 ? (
            <Nenhum />
          ) : (
            facetas.situacoes.map((s) => (
              <Opcao
                key={s.situacao}
                marcada={filtro.situacoes.includes(s.situacao)}
                rotulo={SITUACAO[s.situacao] ?? `Código ${s.situacao}`}
                contagem={s.notas}
                alerta={SITUACOES_SEM_XML.has(s.situacao)}
                aoClicar={() => alternar('situacoes', s.situacao)}
              />
            ))
          )}
        </Bloco>
      </div>
    </div>
  );
}

/* ── Competência: ano expansível em meses ───────────────────────────────── */

function Competencias({
  facetas,
  selecionadas,
  aoDefinir,
}: {
  facetas: Facetas;
  selecionadas: string[];
  aoDefinir: (v: string[]) => void;
}) {
  const [abertos, setAbertos] = useState<number[]>([]);

  /** Marcar o ano substitui a seleção dos meses dele, e vice-versa. */
  function alternarAno(ano: number) {
    const chave = String(ano);
    const marcado = selecionadas.includes(chave);
    const semEsteAno = selecionadas.filter(
      (c) => c !== chave && !c.startsWith(`${chave}-`),
    );
    aoDefinir(marcado ? semEsteAno : [...semEsteAno, chave]);
  }

  function alternarMes(ano: number, mes: number) {
    const chave = `${ano}-${String(mes).padStart(2, '0')}`;
    const anoInteiro = String(ano);

    let base = selecionadas;
    // Se o ano inteiro estava marcado, marcar um mês significa detalhar: troca
    // o ano pelos seus meses, menos o que acabou de ser desmarcado.
    if (base.includes(anoInteiro)) {
      const doAno = facetas.competencias.find((c) => c.ano === ano);
      base = base
        .filter((c) => c !== anoInteiro)
        .concat(
          (doAno?.meses ?? []).map(
            (m) => `${ano}-${String(m.mes).padStart(2, '0')}`,
          ),
        );
    }

    aoDefinir(
      base.includes(chave) ? base.filter((c) => c !== chave) : [...base, chave],
    );
  }

  function estadoAno(ano: number): 'todos' | 'parcial' | 'nenhum' {
    if (selecionadas.includes(String(ano))) return 'todos';
    const meses = selecionadas.filter((c) => c.startsWith(`${ano}-`));
    if (meses.length === 0) return 'nenhum';
    const total =
      facetas.competencias.find((c) => c.ano === ano)?.meses.length ?? 0;
    return meses.length >= total ? 'todos' : 'parcial';
  }

  const todosOsAnos = facetas.competencias.map((c) => String(c.ano));

  return (
    <Bloco
      titulo="Competência"
      selecionados={selecionadas}
      aoMarcarTodos={() => aoDefinir(todosOsAnos)}
      aoLimpar={() => aoDefinir([])}
      nota="Data do documento no SPED. Abra o ano para escolher meses."
    >
      {facetas.competencias.length === 0 ? (
        <Nenhum />
      ) : (
        <div className={css.anos}>
          {facetas.competencias.map((c) => {
            const aberto = abertos.includes(c.ano);
            const estado = estadoAno(c.ano);
            return (
              <div key={c.ano} className={css.ano}>
                <div className={css.anoLinha}>
                  <CaixaTresEstados
                    estado={estado}
                    aoClicar={() => alternarAno(c.ano)}
                    rotulo={`Ano ${c.ano}`}
                  >
                    <span className={css.opcaoLinha}>
                      <span className={css.anoRotulo}>{c.ano}</span>
                      <span className={css.contagem}>{numero(c.notas)}</span>
                    </span>
                  </CaixaTresEstados>
                  <Botao
                    aparencia="discreto"
                    pequeno
                    className={css.anoAbrir}
                    iconeDireita={
                      <ChevronRight
                        className={cx(css.seta, aberto && css.setaAberta)}
                      />
                    }
                    onClick={() =>
                      setAbertos((a) =>
                        aberto ? a.filter((x) => x !== c.ano) : [...a, c.ano],
                      )
                    }
                    aria-expanded={aberto}
                    aria-label={`${aberto ? 'Fechar' : 'Abrir'} os meses de ${c.ano}`}
                  >
                    {plural(c.meses.length, 'mês', 'meses')}
                  </Botao>
                </div>

                {aberto && (
                  <div className={css.meses}>
                    {c.meses.map((m) => {
                      const chave = `${c.ano}-${String(m.mes).padStart(2, '0')}`;
                      return (
                        <Opcao
                          key={m.mes}
                          marcada={
                            selecionadas.includes(chave) ||
                            selecionadas.includes(String(c.ano))
                          }
                          rotulo={MESES[m.mes - 1]}
                          contagem={m.notas}
                          aoClicar={() => alternarMes(c.ano, m.mes)}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Bloco>
  );
}

/* ── CFOP: lista longa, com busca ───────────────────────────────────────── */

function Cfops({
  facetas,
  selecionados,
  aoAlternar,
  aoDefinir,
}: {
  facetas: Facetas;
  selecionados: string[];
  aoAlternar: (cfop: string) => void;
  aoDefinir: (v: string[]) => void;
}) {
  const [busca, setBusca] = useState('');

  const visiveis = useMemo(() => {
    const q = busca.trim();
    return q ? facetas.cfops.filter((c) => c.cfop.includes(q)) : facetas.cfops;
  }, [facetas.cfops, busca]);

  return (
    <Bloco
      titulo="CFOP"
      selecionados={selecionados}
      aoMarcarTodos={() => aoDefinir(facetas.cfops.map((c) => c.cfop))}
      aoLimpar={() => aoDefinir([])}
    >
      {facetas.cfops.length === 0 ? (
        <Nenhum />
      ) : (
        <>
          {facetas.cfops.length > 12 && (
            <Campo
              rotulo="Buscar CFOP"
              rotuloOculto
              icone={<Search />}
              value={busca}
              onChange={(e) => setBusca(e.target.value.replace(/\D/g, ''))}
              placeholder="Buscar CFOP (ex.: 5405)"
              inputMode="numeric"
            />
          )}
          <div className={css.cfops}>
            {visiveis.map((c) => (
              <Opcao
                key={c.cfop}
                marcada={selecionados.includes(c.cfop)}
                rotulo={c.cfop}
                contagem={c.notas}
                mono
                aoClicar={() => aoAlternar(c.cfop)}
              />
            ))}
            {visiveis.length === 0 && (
              <span className={css.nenhum}>Nenhum CFOP com “{busca}”.</span>
            )}
          </div>
        </>
      )}
    </Bloco>
  );
}

/* ── Peças ──────────────────────────────────────────────────────────────── */

function Bloco({
  titulo,
  nota,
  selecionados,
  aoMarcarTodos,
  aoLimpar,
  children,
}: {
  titulo: string;
  nota?: string;
  selecionados: string[];
  aoMarcarTodos?: () => void;
  aoLimpar?: () => void;
  children: ReactNode;
}) {
  const dica =
    nota ?? (selecionados.length === 0 ? 'Sem seleção: todos incluídos.' : '');
  return (
    <fieldset className={css.bloco}>
      <legend className="sr-only">{titulo}</legend>
      <div className={css.blocoTopo} aria-hidden="true">
        <span className={css.blocoTitulo}>
          {titulo}
          {selecionados.length > 0 && (
            <Etiqueta tom="info" ponto={false}>
              {plural(selecionados.length, 'marcado')}
            </Etiqueta>
          )}
        </span>
      </div>
      <div className={css.blocoBarra}>
        <span className={css.blocoNota}>{dica}</span>
        <span className={css.blocoAcoes}>
          {aoMarcarTodos && (
            <Botao aparencia="discreto" pequeno onClick={aoMarcarTodos}>
              Marcar todos
            </Botao>
          )}
          {aoLimpar && selecionados.length > 0 && (
            <Botao aparencia="discreto" pequeno onClick={aoLimpar}>
              Limpar
            </Botao>
          )}
        </span>
      </div>
      <div className={css.opcoes}>{children}</div>
    </fieldset>
  );
}

function Opcao({
  marcada,
  rotulo,
  contagem,
  mono,
  alerta,
  aoClicar,
}: {
  marcada: boolean;
  rotulo: string;
  contagem: number;
  mono?: boolean;
  alerta?: boolean;
  aoClicar: () => void;
}) {
  return (
    <CaixaSelecao
      checked={marcada}
      onChange={aoClicar}
      className={cx(css.opcao, marcada && css.opcaoMarcada)}
    >
      <span className={css.opcaoLinha}>
        <span className={cx(css.opcaoRotulo, mono && css.mono)}>
          {rotulo}
          {alerta && (
            <span
              className={css.alerta}
              title="Sem XML disponível para download"
            >
              <AlertTriangle aria-hidden="true" />
              <span className="sr-only">
                (sem XML disponível para download)
              </span>
            </span>
          )}
        </span>
        <span className={css.contagem}>{numero(contagem)}</span>
      </span>
    </CaixaSelecao>
  );
}

/**
 * Caixa de três estados: usada no ano, que pode estar parcialmente marcado.
 * O `indeterminate` só existe como propriedade do DOM, não como atributo —
 * por isso é ligado num efeito.
 */
function CaixaTresEstados({
  estado,
  aoClicar,
  rotulo,
  children,
}: {
  estado: 'todos' | 'parcial' | 'nenhum';
  aoClicar: () => void;
  rotulo: string;
  children: ReactNode;
}) {
  const raiz = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const caixa = raiz.current?.querySelector('input');
    if (caixa) caixa.indeterminate = estado === 'parcial';
  }, [estado]);

  return (
    <span ref={raiz} className={css.tresEstados}>
      <CaixaSelecao
        checked={estado === 'todos'}
        onChange={aoClicar}
        aria-label={rotulo}
        className={cx(css.opcao, estado !== 'nenhum' && css.opcaoMarcada)}
      >
        {children}
      </CaixaSelecao>
    </span>
  );
}

function Nenhum() {
  return <span className={css.nenhum}>Nada a filtrar aqui.</span>;
}
